/**
 * CP-060 GAP-P1-14 — testable Route Create flow (Application → source → CP-059 → cargo).
 * Mirrors RouteCreateForm / routesApi.getAvailableCargo without UI.
 */
import {
  assertApplicationSelectedBeforeCargo,
  buildWarehouseAvailableCargoQuery,
  type SuccessorRouteCreateContext
} from './cp060RouteCreateFlowHelpers';

export type RouteCreatePhysicalSource = 'application' | 'warehouse';

export type RouteCreateFlowState = {
  applicationId?: number | null;
  loadingPointType?: RouteCreatePhysicalSource;
  loadingPointId?: number | null;
  excludeRouteId?: number;
  successorContext?: SuccessorRouteCreateContext;
};

/** RC-1 + RC-2: application selected; physical source chosen with id. */
export function canQueryAvailableCargo(state: RouteCreateFlowState): boolean {
  if (!assertApplicationSelectedBeforeCargo(state.applicationId)) {
    return false;
  }
  if (state.loadingPointType !== 'application' && state.loadingPointType !== 'warehouse') {
    return false;
  }
  const pid = Number(state.loadingPointId);
  if (!Number.isFinite(pid) || pid <= 0) {
    return false;
  }
  if (state.loadingPointType === 'application') {
    return pid === Number(state.applicationId);
  }
  return true;
}

/** RC-3: params for GET /routes/cargo/available (CP-059 only). */
export function buildAvailableCargoQueryParams(state: RouteCreateFlowState) {
  if (!canQueryAvailableCargo(state)) {
    return null;
  }
  if (state.loadingPointType === 'warehouse') {
    return buildWarehouseAvailableCargoQuery(Number(state.loadingPointId), state.excludeRouteId);
  }
  return {
    loadingPointType: 'application' as const,
    loadingPointId: Number(state.applicationId),
    ...(state.excludeRouteId != null ? { excludeRouteId: state.excludeRouteId } : {})
  };
}

/** RC-1: cargo pick allowed only after application + resolvable availability query. */
export function canEnableCargoSelection(state: RouteCreateFlowState): boolean {
  return buildAvailableCargoQueryParams(state) != null;
}

/** SUCCESSOR_ROUTE: CP-059 queries exclude predecessor route allocations. */
export function resolveRouteCreateExcludeRouteId(params: {
  editingRouteId?: number | null;
  successorContext?: SuccessorRouteCreateContext;
}): number | undefined {
  const editId = params.editingRouteId != null ? Number(params.editingRouteId) : NaN;
  if (Number.isFinite(editId) && editId > 0) {
    return Math.trunc(editId);
  }
  if (params.successorContext?.createContext === 'SUCCESSOR_ROUTE') {
    const pred = params.successorContext.predecessorRouteId;
    const n = pred != null ? Number(pred) : NaN;
    if (Number.isFinite(n) && n > 0) {
      return Math.trunc(n);
    }
  }
  return undefined;
}

export function buildRouteCreateFlowStateFromForm(params: {
  applicationId?: unknown;
  loadingPointType?: unknown;
  loadingPointId?: unknown;
  editingRouteId?: number | null;
  successorContext?: SuccessorRouteCreateContext;
}): RouteCreateFlowState {
  const applicationId =
    params.applicationId != null && Number.isFinite(Number(params.applicationId))
      ? Number(params.applicationId)
      : null;
  const loadingPointType =
    params.loadingPointType === 'application' || params.loadingPointType === 'warehouse'
      ? params.loadingPointType
      : undefined;
  const loadingPointId =
    params.loadingPointId != null && Number.isFinite(Number(params.loadingPointId))
      ? Number(params.loadingPointId)
      : null;
  return {
    applicationId,
    loadingPointType,
    loadingPointId,
    excludeRouteId: resolveRouteCreateExcludeRouteId({
      editingRouteId: params.editingRouteId,
      successorContext: params.successorContext
    })
  };
}

/** Legacy / Successor create — block cargo step and submit without consistent physical source. */
export function validateLegacyRouteCreateFlowForCargo(
  state: RouteCreateFlowState
): { ok: true } | { ok: false; message: string } {
  if (!assertApplicationSelectedBeforeCargo(state.applicationId)) {
    return { ok: false, message: 'Выберите заявку перед выбором груза' };
  }
  if (state.loadingPointType !== 'application' && state.loadingPointType !== 'warehouse') {
    return { ok: false, message: 'Выберите физический источник погрузки' };
  }
  const pid = Number(state.loadingPointId);
  if (!Number.isFinite(pid) || pid <= 0) {
    return { ok: false, message: 'Выберите физический источник погрузки' };
  }
  if (state.loadingPointType === 'application' && pid !== Number(state.applicationId)) {
    return {
      ok: false,
      message: 'Источник «заявка» не соответствует выбранной заявке — выберите источник заново'
    };
  }
  if (!canQueryAvailableCargo(state)) {
    return { ok: false, message: 'Физический источник не согласован с заявкой' };
  }
  return { ok: true };
}

/** RC-4: legacy main loading stop binds physical warehouse (not address-only). */
export function buildLegacyMainLoadingStopPayload(finalData: {
  loadingPointType?: RouteCreatePhysicalSource | string | null;
  loadingPointId?: number | null;
  loadingAddress?: string | null;
}) {
  const pointType = finalData.loadingPointType ?? null;
  const pointId = finalData.loadingPointId ?? null;
  return {
    type: 'loading' as const,
    position: 0,
    address: finalData.loadingAddress ?? null,
    pointType,
    pointId
  };
}

export function isWarehousePhysicalLoadingStop(
  stop: { pointType?: string | null; pointId?: number | null },
  warehouseId: number
): boolean {
  return stop.pointType === 'warehouse' && Number(stop.pointId) === warehouseId;
}

/** Successor deep link seeds application (and optional warehouse) without bypassing CP-059. */
/**
 * Phase 3A group-UI topology is not used for CP-060 SUCCESSOR_ROUTE create (carrier handoff).
 * Successor deep link always uses legacy step flow where RC-1 application gate applies.
 */
export function isSuccessorRouteCreateContext(
  ctx: SuccessorRouteCreateContext | undefined
): boolean {
  return ctx?.createContext === 'SUCCESSOR_ROUTE';
}

export function resolveRouteCreateUsesGroupTopology(params: {
  groupUiFeatureFlag: boolean;
  editingRoute: boolean;
  successorContext?: SuccessorRouteCreateContext;
}): boolean {
  if (params.editingRoute) {
    return params.groupUiFeatureFlag;
  }
  if (isSuccessorRouteCreateContext(params.successorContext)) {
    return false;
  }
  return params.groupUiFeatureFlag;
}

/** W2: successor preselects application only; warehouse after physical-sources list (no directory fallback). */
export function applySuccessorRouteCreatePrefill(
  ctx: SuccessorRouteCreateContext | undefined
): Partial<{
  applicationId: number;
}> {
  if (!ctx || ctx.createContext !== 'SUCCESSOR_ROUTE') {
    return {};
  }
  if (ctx.applicationId == null) {
    return {};
  }
  return { applicationId: ctx.applicationId };
}

/** When user picks «По заявке», loading stop id tracks selected application (RC-2). */
export function syncApplicationLoadingPoint(applicationId: number): {
  loadingPointType: 'application';
  loadingPointId: number;
  applicationId: number;
} {
  return {
    loadingPointType: 'application',
    loadingPointId: applicationId,
    applicationId
  };
}

/** Warehouse source selection (RC-2 / RC-4). */
export function syncWarehouseLoadingPoint(warehouseId: number): {
  loadingPointType: 'warehouse';
  loadingPointId: number;
} {
  return {
    loadingPointType: 'warehouse',
    loadingPointId: warehouseId
  };
}
